/**
 * ImportCSVMapperModal — Modal genérico de 3 fases para importar CSV con mapeo dinámico.
 * Fase 1: Subir archivo  →  Fase 2: Mapear columnas  →  Fase 3: Resultados
 *
 * Componente COMPARTIDO. Vivía en `modules/sales/components/` y lo consumían
 * cuatro módulos importándolo desde ahí (cuentas contables, proveedores,
 * clientes y productos); se movió acá al sumar los importadores de RRHH.
 * Cualquier módulo que necesite carga masiva declara sus `systemFields` y su
 * `onImport`, y no reimplementa el flujo.
 */
import { useState, useRef, useCallback, useMemo } from 'react'
import { DataTable } from '@/components/ds/DataTable'
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, Download, ArrowLeft, ArrowRight, Columns3 } from 'lucide-react'
import {
  parseCSVPreview, autoMatch,
  type SystemField,
} from '@/lib/csvUtils'
import { Modal } from '@/components/ds/Modal'

// Re-export for consumers (AccountsTab, CustomersPage, ProductsPage)
export type { SystemField }

export interface CSVImportResult {
  created: number
  updated: number
  errors: { row: number; error: string | Record<string, string[]> }[]
  total_rows: number
  /** Campos opcionales que usan los importadores con dry-run (ver `onDryRun`). */
  dry_run?: boolean
  skipped?: number
  warnings?: { row: number; warning: string }[]
  quota?: { limit: number | null; current: number; after_import: number; fits: boolean; message?: string }
}

/** Encabezados y muestra que devuelve un preview hecho en el servidor. */
export interface RemotePreview {
  headers: string[]
  rows: string[][]
}

interface ImportCSVMapperModalProps {
  open: boolean
  onClose: () => void
  title: string
  description: string
  systemFields: SystemField[]
  templateFileName: string
  onImport: (file: File, mapping: Record<string, string>) => Promise<CSVImportResult>
  isPending: boolean
  /**
   * Si se pasa, el archivo se previsualiza en el SERVIDOR en vez del navegador.
   * Necesario para aceptar Excel: un .xlsx es un ZIP binario y parsearlo acá
   * exigiría sumar una librería al bundle. Con esto, además, lo que el usuario
   * mapea es exactamente lo que el importador va a leer.
   */
  onPreview?: (file: File) => Promise<RemotePreview>
  /**
   * Si se pasa, entre el mapeo y la escritura se intercala una simulación: se
   * muestra qué haría (altas, cambios, errores, cupo) y el usuario recién ahí
   * confirma. Sin esto el flujo es el de siempre: mapear e importar.
   */
  onDryRun?: (file: File, mapping: Record<string, string>) => Promise<CSVImportResult>
  /** Extensiones aceptadas. Por defecto solo `.csv`. */
  accept?: string[]
}

// ─── Component ───────────────────────────────────────────────────────────────

type Phase = 'upload' | 'map' | 'confirm' | 'result'

export function ImportCSVMapperModal({
  open, onClose, title, description, systemFields, templateFileName, onImport, isPending,
  onPreview, onDryRun, accept = ['.csv'],
}: ImportCSVMapperModalProps) {
  const [phase, setPhase] = useState<Phase>('upload')
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [csvHeaders, setCsvHeaders] = useState<string[]>([])
  const [previewRows, setPreviewRows] = useState<string[][]>([])
  // Filas del archivo completo: se muestran antes de importar para que nadie
  // espere sin saber cuánto va a tardar.
  const [totalRows, setTotalRows] = useState<number | null>(null)
  const [mapping, setMapping] = useState<Record<string, string>>({})
  const [result, setResult] = useState<CSVImportResult | null>(null)
  const [dryRunResult, setDryRunResult] = useState<CSVImportResult | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const reset = useCallback(() => {
    setPhase('upload')
    setFile(null)
    setCsvHeaders([])
    setPreviewRows([])
    setMapping({})
    setResult(null)
    setDryRunResult(null)
    setBusy(false)
    setError(null)
  }, [])

  const handleClose = () => {
    reset()
    onClose()
  }

  // ─── Phase 1: Upload ────────────────────────────────────────────────────────

  const handleFile = async (f: File) => {
    const nombre = f.name.toLowerCase()
    if (!accept.some(ext => nombre.endsWith(ext))) {
      setError(`Solo se permiten archivos ${accept.join(', ')}`)
      return
    }
    setError(null)
    setResult(null)
    setDryRunResult(null)
    setFile(f)

    const aplicar = (headers: string[], rows: string[][]) => {
      if (headers.length === 0) {
        setError('El archivo está vacío o no tiene encabezados.')
        return
      }
      setCsvHeaders(headers)
      setPreviewRows(rows)
      setMapping(autoMatch(headers, systemFields))
      setPhase('map')
    }

    // Excel no se puede parsear en el navegador sin sumar una librería: cuando el
    // consumidor provee `onPreview`, el servidor lo lee con el mismo lector que
    // después importa.
    const esCsv = nombre.endsWith('.csv')
    if (onPreview && !esCsv) {
      setBusy(true)
      try {
        const { headers, rows } = await onPreview(f)
        aplicar(headers, rows)
      } catch (e: any) {
        setError(e?.response?.data?.error?.message || e?.message || 'No se pudo leer el archivo.')
      } finally {
        setBusy(false)
      }
      return
    }

    const reader = new FileReader()
    reader.onload = (ev) => {
      const { headers, rows, totalRows: n } = parseCSVPreview(ev.target?.result as string)
      setTotalRows(n)
      aplicar(headers, rows)
    }
    reader.readAsText(f)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  const downloadTemplate = () => {
    const bom = '\uFEFF'
    const headers = systemFields.map(f => f.key)
    const csv = bom + headers.join(',') + '\n'
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = templateFileName
    a.click()
    URL.revokeObjectURL(url)
  }

  // ─── Phase 2: Mapping ──────────────────────────────────────────────────────

  const updateMapping = (csvHeader: string, fieldKey: string) => {
    setMapping(prev => {
      const next = { ...prev }
      if (fieldKey === '') {
        delete next[csvHeader]
      } else {
        // Remove any other header mapped to the same field
        for (const [k, v] of Object.entries(next)) {
          if (v === fieldKey && k !== csvHeader) delete next[k]
        }
        next[csvHeader] = fieldKey
      }
      return next
    })
  }

  const requiredFields = useMemo(() => systemFields.filter(f => f.required), [systemFields])

  const missingRequired = useMemo(() => {
    const mappedValues = new Set(Object.values(mapping))
    return requiredFields.filter(f => !mappedValues.has(f.key))
  }, [mapping, requiredFields])

  const canImport = missingRequired.length === 0

  // ─── Phase 3: Import ───────────────────────────────────────────────────────

  /** Paso siguiente desde el mapeo: simular si hay dry-run, o importar directo. */
  const handleNext = async () => {
    if (!file || !canImport) return
    setError(null)
    if (!onDryRun) return handleImport()
    setBusy(true)
    try {
      setDryRunResult(await onDryRun(file, mapping))
      setPhase('confirm')
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || e?.message || 'Error al simular la importación.')
    } finally {
      setBusy(false)
    }
  }

  const handleImport = async () => {
    if (!file || !canImport) return
    setError(null)
    setBusy(true)
    try {
      const res = await onImport(file, mapping)
      setResult(res)
      setPhase('result')
    } catch (e: any) {
      const msg = e?.response?.data?.error?.message || e?.message || 'Error al importar.'
      setError(msg)
    } finally {
      setBusy(false)
    }
  }

  const working = isPending || busy
  const totalPasos = onDryRun ? 4 : 3
  // Cuántas filas se van a escribir, para rotular el botón con el número real.
  const aImportar = (dryRunResult?.created ?? 0) + (dryRunResult?.updated ?? 0)
  // El cupo del plan es la única condición que bloquea confirmar: con filas con
  // error el resto sí puede entrar, y decidirlo es del usuario.
  const puedeConfirmar = aImportar > 0 && (dryRunResult?.quota?.fits ?? true)

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (!open) return null

  return (
    <Modal
      open
      onClose={handleClose}
      size="lg"
      title={
        <span className="flex items-center gap-2">
          <FileSpreadsheet size={18} className="text-primary shrink-0" />
          {title}
        </span>
      }
      subtitle={
        <>
          {phase === 'map' && (
            <span className="text-xs text-content-subtle bg-surface-muted px-2 py-0.5 rounded-full">
              Paso 2/{totalPasos} — Mapear columnas
            </span>
          )}
          {phase === 'confirm' && (
            <span className="text-xs text-content-subtle bg-surface-muted px-2 py-0.5 rounded-full">
              Paso 3/4 — Revisar antes de importar
            </span>
          )}
          {phase === 'result' && (
            <span className="text-xs text-success-content-soft bg-success-surface px-2 py-0.5 rounded-full">
              Paso {totalPasos}/{totalPasos} — Resultados
            </span>
          )}
        </>
      }
      actions={
        <>
          <div className="flex flex-1 flex-wrap items-center justify-between gap-2">
          <div>
            {phase === 'map' && (
              <button onClick={() => { setPhase('upload'); setFile(null); setCsvHeaders([]); setPreviewRows([]); setMapping({}) }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                <ArrowLeft size={14} />
                Volver
              </button>
            )}
            {phase === 'confirm' && (
              <button onClick={() => { setPhase('map'); setDryRunResult(null) }}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                <ArrowLeft size={14} />
                Ajustar mapeo
              </button>
            )}
          </div>

          <div className="flex gap-3">
            {phase === 'result' ? (
              <button onClick={handleClose}
                className="px-4 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover transition-colors">
                Cerrar
              </button>
            ) : phase === 'upload' ? (
              <button onClick={handleClose}
                className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                Cancelar
              </button>
            ) : phase === 'confirm' ? (
              <>
                <button onClick={handleClose}
                  className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                  Cancelar
                </button>
                <button onClick={handleImport} disabled={working || !puedeConfirmar}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                  {working ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {aImportar > 0 ? `Importar ${aImportar}` : 'Importar'}
                </button>
              </>
            ) : (
              <>
                <button onClick={handleClose}
                  className="px-4 py-2 text-sm text-content-muted border border-divider rounded-lg hover:bg-soft transition-colors">
                  Cancelar
                </button>
                <button onClick={handleNext} disabled={!canImport || working}
                  className="inline-flex items-center gap-2 px-5 py-2 bg-primary text-on-primary text-sm font-medium rounded-lg hover:bg-primary-hover disabled:opacity-60 transition-colors">
                  {working ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {onDryRun ? 'Revisar' : 'Importar'}
                </button>
              </>
            )}
          </div>
          </div>
        </>
      }
    >
      <div className="space-y-4">
        {/* ── Phase 1: Upload ─────────────────────────────────────────── */}
        {phase === 'upload' && (
          <>
            <p className="text-sm text-content-muted">{description}</p>

            <button onClick={downloadTemplate}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-primary border border-primary-200 rounded-lg hover:bg-primary-50 transition-colors">
              <Download size={14} />
              Descargar plantilla CSV
            </button>

            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => inputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all
                ${dragOver ? 'border-primary-200 bg-primary-50' : 'border-divider hover:border-divider-strong hover:bg-soft'}`}
            >
              {busy
                ? <Loader2 size={28} className="mx-auto mb-2 text-primary animate-spin" />
                : <Upload size={28} className="mx-auto mb-2 text-content-subtle" />}
              <p className="text-sm text-content-muted">
                {busy
                  ? 'Leyendo el archivo…'
                  : `Arrastra un archivo ${accept.join(' o ')} aquí o haz clic para seleccionar`}
              </p>
              <p className="text-xs text-content-subtle mt-1">
                {accept.includes('.xlsx')
                  ? 'CSV (separador coma o punto y coma) o Excel .xlsx'
                  : 'Formato: CSV con separador coma, codificación UTF-8'}
              </p>
              <input ref={inputRef} type="file" accept={accept.join(',')} className="hidden"
                onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = '' }} />
            </div>
          </>
        )}

        {/* ── Phase 2: Mapping ───────────────────────────────────────── */}
        {phase === 'map' && (
          <>
            <div className="flex items-center gap-2 text-sm text-content-muted">
              <Columns3 size={14} className="text-content-subtle" />
              <span>
                Archivo: <strong>{file?.name}</strong> — {csvHeaders.length} columnas
                {totalRows !== null && <> · <strong>{totalRows.toLocaleString('es-CL')}</strong> {totalRows === 1 ? 'fila' : 'filas'}</>}
              </span>
            </div>

            {totalRows !== null && totalRows >= 400 && (
              <div className="rounded-lg border border-divider bg-glass-3 px-3 py-2 text-xs text-content-muted">
                Un archivo de este tamaño puede tardar <strong>varios minutos</strong> en
                importarse. No cierres esta pestaña mientras corre: si la cierras, la
                importación se cancela completa y no queda cargada a medias.
              </div>
            )}

            {/*
              El preview de cada columna se calcula ACÁ y no en el `render`:
              sale de `previewRows[n][idx]`, o sea de la POSICIÓN de la columna
              en el archivo, y el `render` recibe el índice de la fila ya
              ordenada. Calculado adentro, cada preview viaja con su columna y
              los dos no se pueden separar.

              🟢 La edición, en cambio, ya iba por identidad: `updateMapping`
              recibe el nombre de la columna, no su posición.
            */}
            <DataTable<{ header: string; previews: string[]; campo: string }>
              columns={[
                { key: 'header', header: 'Columna CSV', width: '25%',
                  render: f => (
                    <span className="font-mono text-xs bg-surface-muted px-1.5 py-0.5 rounded text-content">
                      {f.header}
                    </span>
                  ) },
                { key: 'previews', header: 'Preview', width: '40%',
                  render: f => (
                    <div className="flex flex-wrap gap-1">
                      {f.previews.map((v, i) => (
                        <span key={i} className="text-xs text-content-subtle bg-surface-muted px-1.5 py-0.5 rounded truncate max-w-[140px]">
                          {v}
                        </span>
                      ))}
                      {f.previews.length === 0 && <span className="text-xs text-content-subtle italic">vacío</span>}
                    </div>
                  ) },
                { key: 'campo', header: 'Campo del sistema', width: '35%',
                  render: f => (
                    <select
                      value={f.campo}
                      onChange={e => updateMapping(f.header, e.target.value)}
                      aria-label={`Campo del sistema para la columna ${f.header}`}
                      className={`w-full text-sm border rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-primary transition-colors
                        ${f.campo ? 'border-primary-200 bg-primary-50/50 text-primary-hover' : 'border-divider text-content-muted'}`}
                    >
                      <option value="">— No importar —</option>
                      {systemFields.map(sf => {
                        const alreadyUsed = Object.entries(mapping).some(
                          ([k, v]) => v === sf.key && k !== f.header
                        )
                        return (
                          <option key={sf.key} value={sf.key} disabled={alreadyUsed}>
                            {sf.label}{sf.required ? ' *' : ''}{alreadyUsed ? ' (ya asignado)' : ''}
                          </option>
                        )
                      })}
                    </select>
                  ) },
              ]}
              rows={csvHeaders.map((header, idx) => ({
                header,
                previews: previewRows.map(r => r[idx] || '').filter(Boolean).slice(0, 2),
                campo: mapping[header] || '',
              }))}
              rowKey={f => f.header}
              caption="Correspondencia entre las columnas del archivo y los campos del sistema"
            />

            {/* Missing required fields warning */}
            {missingRequired.length > 0 && (
              <div className="flex items-start gap-2 p-3 bg-warning-surface border border-warning-border rounded-lg">
                <AlertTriangle size={16} className="text-warning-solid mt-0.5 shrink-0" />
                <p className="text-sm text-warning-content-strong">
                  Campos obligatorios sin mapear:{' '}
                  <strong>{missingRequired.map(f => f.label).join(', ')}</strong>
                </p>
              </div>
            )}
          </>
        )}

        {/* ── Phase 3 (opcional): Confirmación tras el dry-run ───────── */}
        {phase === 'confirm' && dryRunResult && (
          <ImportSummary result={dryRunResult} simulacion />
        )}

        {/* ── Phase final: Resultados ────────────────────────────────── */}
        {phase === 'result' && result && (
          <ImportSummary result={result} />
        )}

        {/* Error banner */}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-danger-surface border border-danger-border rounded-lg">
            <AlertTriangle size={16} className="text-danger-solid mt-0.5 shrink-0" />
            <p className="text-sm text-danger-content">{error}</p>
          </div>
        )}
      </div>
    </Modal>
  )
}

// ─── Resumen ─────────────────────────────────────────────────────────────────

/**
 * Resumen de una importación. Se usa en las dos fases finales: con
 * `simulacion` describe lo que **va a pasar**, y sin ella lo que **pasó**. Es el
 * mismo cuerpo a propósito — si la simulación se viera distinta del resultado,
 * el usuario no podría confiar en que confirmó lo que vio.
 */
function ImportSummary({ result, simulacion = false }: {
  result: CSVImportResult
  simulacion?: boolean
}) {
  const errores = result.errors ?? []
  const avisos = result.warnings ?? []
  const cupo = result.quota
  const cupoExcedido = cupo && !cupo.fits
  const aEscribir = result.created + result.updated

  return (
    <div className="space-y-3">
      {/* Encabezado del resumen */}
      <div className={`flex items-start gap-2 p-3 rounded-lg border ${
        cupoExcedido
          ? 'bg-danger-surface border-danger-border'
          : simulacion
            ? 'bg-glass-3 border-divider'
            : 'bg-success-surface border-success-border'
      }`}>
        {cupoExcedido
          ? <AlertTriangle size={16} className="text-danger-solid mt-0.5 shrink-0" />
          : simulacion
            ? <Columns3 size={16} className="text-content-subtle mt-0.5 shrink-0" />
            : <CheckCircle2 size={16} className="text-success-solid mt-0.5 shrink-0" />}
        <div className={`text-sm ${
          cupoExcedido
            ? 'text-danger-content'
            : simulacion
              ? 'text-content'
              : 'text-success-content-strong'
        }`}>
          <p className="font-medium">
            {cupoExcedido
              ? 'No se puede importar: excede el plan'
              : simulacion
                ? 'Esto es lo que va a pasar. Todavía no se ha escrito nada.'
                : 'Importación completada'}
          </p>
          <p className="mt-1">
            {result.total_rows} {result.total_rows === 1 ? 'fila' : 'filas'}:{' '}
            <strong>{result.created} {simulacion ? 'se crearán' : 'creados'}</strong>,{' '}
            <strong>{result.updated} {simulacion ? 'se actualizarán' : 'actualizados'}</strong>
            {errores.length > 0 && <>,{' '}
              <strong className="text-warning-content">
                {errores.length} {errores.length === 1 ? 'con error' : 'con errores'}
              </strong>
            </>}
          </p>
          {simulacion && errores.length > 0 && aEscribir > 0 && (
            <p className="mt-1 text-xs text-content-muted">
              Las filas con error se omiten; el resto se importa igual.
            </p>
          )}
        </div>
      </div>

      {/* Cupo del plan */}
      {cupo && cupo.limit !== null && (
        <div className="flex items-center justify-between px-3 py-2 text-xs border border-divider rounded-lg">
          <span className="text-content-muted">Trabajadores activos del plan</span>
          <span className={cupoExcedido ? 'font-semibold text-danger-content-soft' : 'text-content'}>
            {cupo.current} → <strong>{cupo.after_import}</strong> de {cupo.limit}
          </span>
        </div>
      )}
      {cupoExcedido && cupo?.message && (
        <p className="text-sm text-danger-content">{cupo.message}</p>
      )}

      {/* Errores por fila */}
      {errores.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-content-muted mb-1">
            {simulacion ? 'Filas que se omitirán' : 'Filas con error'}
          </p>
          {/* El encabezado queda fijo dentro de la lista, que tiene alto propio:
              es `maxHeight` del DataTable, no un `sticky` desde afuera (ver su
              docstring: el `overflow` del envoltorio se lo quitaría). */}
          <DataTable<(typeof errores)[number] & { i: number }>
            caption="Filas con error"
            maxHeight={160}
            minWidth={320}
            rows={errores.map((err, i) => ({ ...err, i }))}
            rowKey={err => err.i}
            columns={[
              { key: 'fila', header: 'Fila', width: 64,
                render: err => <span className="font-mono">{err.row || '—'}</span> },
              { key: 'motivo', header: 'Motivo',
                render: err => (
                  <>
                    {typeof err.error === 'string'
                ? err.error
                : Object.values(err.error)
                    .map(v => (Array.isArray(v) ? v.join(', ') : v))
                    .join('; ')}
                  </>
                ) },
            ]}
          />
        </div>
      )}

      {/* Advertencias: la fila entra, pero conviene mirarla */}
      {avisos.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-content-muted mb-1">
            Advertencias ({avisos.length}) — estas filas {simulacion ? 'se importarán' : 'se importaron'} igual
          </p>
          {/*
            ⚠️ Y esta es una lista sin encabezados: migrarla le agregaría
            columnas nombradas que hoy no tiene, sobre dos datos que no las
            necesitan (la fila y su aviso).
          */}
          <div className="max-h-40 overflow-y-auto border border-warning-border rounded-lg">
            <table className="w-full text-xs">
              <tbody className="divide-y divide-amber-200/60 dark:divide-amber-800/60">
                {avisos.map((av, idx) => (
                  <tr key={idx} className="text-warning-content-strong">
                    <td className="px-3 py-1.5 font-mono w-16 align-top">{av.row || '—'}</td>
                    <td className="px-3 py-1.5">{av.warning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
